// Naming the people in a triage result (TGCLOUD-1910).
//
// A result never carries a name: `riskfactors` is not a Queryable Encryption
// collection, so the server keeps people by member id, pedigree number and
// relationship only. The panel names them from the pedigree it has already
// loaded (`memberRecords`), and not at all on an anonymous deployment, where
// the chart shows no names either: there they read as their relationship and
// UPN.
import React from 'react';
import { english } from 'src/i18n/language';
import { relationText } from './triageText';

/** Who in `memberRecords` a result's person reference is. */
export const memberFor = (ref, memberRecords) => {
  if (!ref || !Array.isArray(memberRecords)) return null;
  const ids = [ref.memberId, ref.familyMemberId].filter(Boolean).map(String);
  return (
    memberRecords.find(
      (m) =>
        (m?._id && ids.includes(String(m._id))) ||
        (m?.familyMemberId && ids.includes(String(m.familyMemberId))) ||
        (m?.proBandId && ids.includes(String(m.proBandId)))
    ) ||
    (ref.pid !== null && ref.pid !== undefined
      ? memberRecords.find((m) => String(m?.pid) === String(ref.pid))
      : null) ||
    null
  );
};

const nameOfMember = (member, anonymous) =>
  anonymous || !member || member.isHide
    ? ''
    : [member.firstName, member.lastName].filter(Boolean).join(' ').trim();

/**
 * "Jane Doe (maternal aunt)", "maternal aunt, UPN 4", "this person".
 *
 * @param {object} ref       the result's `people[key]`
 * @param {object} o
 * @param {object[]} [o.memberRecords]
 * @param {boolean} [o.anonymous]  the fail-closed flag (isAnonymousOrLoading)
 * @param {Function} [o.t]
 */
export const personLabel = (
  ref,
  { memberRecords, anonymous = true, t = english } = {}
) => {
  if (!ref) return t('a relative');
  const member = memberFor(ref, memberRecords);
  const name = nameOfMember(member, anonymous);
  if (ref.isIndex)
    return name ? t('{{name}} (this person)', { name }) : t('this person');
  const relation = relationText(ref.relation, ref.sex, t);
  if (name) return t('{{name}} ({{relation}})', { name, relation });
  const upn = member?.upn;
  if (upn !== undefined && upn !== null && upn !== '')
    return t('{{relation}}, UPN {{upn}}', { relation, upn });
  return relation;
};

/** `nameOf(key)` for triageText, over one result's `people`. */
export const namerFor = (people, o) => (key) => personLabel(people?.[key], o);

/**
 * The pedigree around the panel: who is who, and how to show and select a
 * person on the chart. Provided by RiskFactorsMenu; a stored result opened
 * anywhere else reads the defaults, and names people by relationship only.
 */
export const TriagePeopleContext = React.createContext({
  memberRecords: null,
  anonymous: true,
  onSelectMember: null
});

export const useTriagePeople = () => React.useContext(TriagePeopleContext);
